import { useCallback, useRef, useState } from 'react';

/**
 * salvaCampo — il salvataggio automatico, un campo per volta.
 *
 * QUANDO IL MEDICO ESCE DA UN CAMPO, quel campo si salva. Niente bozza:
 * su una cartella clinica quello che e' stato scritto e' scritto, e se
 * il browser muore a meta' visita non si perde niente. Il prezzo -
 * anche un refuso resta in cartella - e' stato accettato il 22 settembre
 * 2026: l'audit tiene lo storico per campo e si vede cosa e' cambiato.
 *
 * TRE STATI, E UNO NON SPARISCE MAI DA SOLO:
 *   in_corso     sta andando;
 *   salvato      andato: la spunta sbiadisce dopo qualche secondo,
 *                perche' un successo che resta a schermo diventa rumore;
 *   non_salvato  NON sbiadisce. Un medico che perde la rete per un
 *                minuto deve poter tornare indietro e vedere quali
 *                campi non sono passati: un avviso che sparisce dopo
 *                tre secondi non glielo dice.
 *
 * NESSUN RITENTATIVO AUTOMATICO. Un ritentativo che riscrive da solo in
 * una cartella clinica e' una scrittura che nessuno ha chiesto. Si
 * riprova cliccando, ed e' un gesto di una persona.
 *
 * UNA CODA PER CAMPO, non un numero di versione. Se una richiesta per
 * quel campo e' gia' in volo, la seconda non parte in parallelo:
 * aspetta, e se nel frattempo il testo cambia ancora si manda SOLO
 * l'ultimo valore. Cosi' due modifiche rapide sullo stesso campo fanno
 * una scrittura sola, con il valore giusto, e due risposte non possono
 * tornare invertite. Il numero di versione (una colonna in piu', 409 se
 * qualcuno ha scritto nel frattempo) serve quando due medici scrivono
 * lo stesso campo nello stesso minuto: si aggiunge il giorno che
 * succede.
 *
 * IL TESTO NON SALVATO NON VA IN `localStorage`, NE' IN
 * `sessionStorage`, NE' IN IndexedDB. Sarebbe la soluzione ovvia per non
 * perderlo a un ricaricamento, e non si fa: le postazioni della clinica
 * sono condivise, e quel testo e' una riga di cartella clinica che
 * resterebbe sul disco di quel computer, leggibile dal prossimo che si
 * siede li', fuori da ogni permesso e da ogni traccia. Conseguenza
 * accettata: ricaricando la pagina con dei campi rossi, quel testo si
 * perde. La protezione e' l'avviso in uscita, che c'e' gia'.
 *
 * @param {Function} scrivi     (campo, valore) => Promise. Una scrittura.
 * @param {Function} onSalvato  chiamata dopo ogni scrittura riuscita: serve a
 *        chi tiene un'istantanea del modulo per sapere che quel pezzo e'
 *        in archivio, e non dire piu' «modifiche non salvate» per tutto.
 */
export function useSalvaCampo(scrivi, onSalvato) {
    const [stato, setStato] = useState({});      // campo -> 'in_corso' | 'salvato' | 'non_salvato'
    const inVolo = useRef({});                   // campo -> true
    const inCoda = useRef({});                   // campo -> ultimo valore in attesa
    const timer = useRef({});

    const segna = (campo, valore) => setStato((p) => ({ ...p, [campo]: valore }));

    const esegui = useCallback(async (campo, valore) => {
        inVolo.current[campo] = true;
        segna(campo, 'in_corso');
        try {
            await scrivi(campo, valore);
            /* Se mentre andava e' arrivato un valore nuovo, lo stato
               giusto non e' «salvato»: c'e' ancora da scrivere. */
            if (!(campo in inCoda.current)) {
                segna(campo, 'salvato');
                if (onSalvato) onSalvato(campo);
                clearTimeout(timer.current[campo]);
                timer.current[campo] = setTimeout(
                    () => setStato((p) => (p[campo] === 'salvato' ? { ...p, [campo]: undefined } : p)),
                    3000);
            }
        } catch (e) {
            segna(campo, 'non_salvato');
            /* Quello che era in coda si butta: non si riprova da soli, e
               tenerlo vorrebbe dire scrivere piu' tardi un valore che
               chi l'ha scritto crede perso. */
            delete inCoda.current[campo];
            inVolo.current[campo] = false;
            return;
        }
        inVolo.current[campo] = false;
        if (campo in inCoda.current) {
            const prossimo = inCoda.current[campo];
            delete inCoda.current[campo];
            await esegui(campo, prossimo);
        }
    }, [scrivi, onSalvato]);

    /** Da chiamare quando il campo perde il fuoco, o quando cambia una scelta. */
    const salva = useCallback((campo, valore) => {
        if (inVolo.current[campo]) { inCoda.current[campo] = valore; return; }
        esegui(campo, valore);
    }, [esegui]);

    const nonSalvati = Object.entries(stato).filter(([, v]) => v === 'non_salvato').map(([k]) => k);

    return { stato, salva, nonSalvati };
}
